export const DEFAULT_ARTIKEL_AUFSCHLAG_PROZENT = 35;

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function aufschlagFromEkVk(einkaufspreis: number, verkaufspreis: number): number {
  if (!einkaufspreis || einkaufspreis <= 0) return 0;
  return roundMoney(((verkaufspreis - einkaufspreis) / einkaufspreis) * 100);
}

export function vkFromEk(einkaufspreis: number, aufschlagProzent: number): number {
  return roundMoney(einkaufspreis * (1 + aufschlagProzent / 100));
}

export function aufschlagBetragFromEkVk(einkaufspreis: number, verkaufspreis: number): number {
  return roundMoney(verkaufspreis - einkaufspreis);
}

export type PricingDriver = "vk" | "aufschlag";
export type AufschlagModus = "prozent" | "betrag";

export function normalizeAufschlagModus(value?: string | null): AufschlagModus {
  return value === "betrag" ? "betrag" : "prozent";
}

export interface ArticlePricingFields {
  einkaufspreis_netto?: number;
  einzelpreis_netto?: number;
  aufschlag_prozent?: number;
  aufschlag_betrag?: number;
  aufschlag_modus?: AufschlagModus | string | null;
  preis_driver?: PricingDriver;
}

/** Verkaufspreis aus EK und dem aktiven Aufschlag (Prozent oder Euro). */
export function kalkulierterVk(
  fields: ArticlePricingFields,
  fallbackEinzelpreis = 0,
): number {
  const ek = roundMoney(Number(fields.einkaufspreis_netto) || 0);
  if (normalizeAufschlagModus(fields.aufschlag_modus) === "betrag") {
    return roundMoney(ek + (Number(fields.aufschlag_betrag) || 0));
  }
  const pct = roundMoney(Number(fields.aufschlag_prozent) || 0);
  if (ek > 0) return vkFromEk(ek, pct);
  return roundMoney(Number(fallbackEinzelpreis) || 0);
}

export function pricingFromDatabase(
  einkaufspreis: number,
  verkaufspreis: number,
): Required<
  Pick<
    ArticlePricingFields,
    "einkaufspreis_netto" | "einzelpreis_netto" | "aufschlag_prozent" | "aufschlag_betrag"
  >
> & {
  aufschlag_modus: AufschlagModus;
  preis_driver: PricingDriver;
} {
  const ek = roundMoney(einkaufspreis);
  const vk = roundMoney(verkaufspreis);
  return {
    einkaufspreis_netto: ek,
    einzelpreis_netto: vk,
    aufschlag_prozent: aufschlagFromEkVk(ek, vk),
    aufschlag_betrag: aufschlagBetragFromEkVk(ek, vk),
    aufschlag_modus: "prozent",
    preis_driver: "vk",
  };
}

function pricingSnapshot(
  ek: number,
  vk: number,
  aufschlagProzent: number,
  aufschlagBetrag: number,
  modus: AufschlagModus,
  driver: PricingDriver,
): ArticlePricingFields & { preis_driver: PricingDriver; aufschlag_modus: AufschlagModus } {
  return {
    einkaufspreis_netto: ek,
    einzelpreis_netto: vk,
    aufschlag_prozent: aufschlagProzent,
    aufschlag_betrag: aufschlagBetrag,
    aufschlag_modus: modus,
    preis_driver: driver,
  };
}

/** EK/VK/Aufschlag: zuletzt geändertes Feld steuert, welcher Wert berechnet wird. */
export function syncPricingField(
  current: ArticlePricingFields,
  field: "ek" | "vk" | "aufschlag",
  value: number,
): ArticlePricingFields & { preis_driver: PricingDriver; aufschlag_modus: AufschlagModus } {
  const driver: PricingDriver =
    field === "vk" ? "vk" : field === "aufschlag" ? "aufschlag" : current.preis_driver ?? "aufschlag";
  const modus = normalizeAufschlagModus(current.aufschlag_modus);
  const ek = field === "ek" ? roundMoney(value) : roundMoney(current.einkaufspreis_netto ?? 0);

  if (field === "vk") {
    const vk = roundMoney(value);
    return pricingSnapshot(
      ek,
      vk,
      aufschlagFromEkVk(ek, vk),
      aufschlagBetragFromEkVk(ek, vk),
      modus,
      "vk",
    );
  }

  if (field === "aufschlag") {
    if (modus === "betrag") {
      const betrag = roundMoney(value);
      const vk = roundMoney(ek + betrag);
      return pricingSnapshot(ek, vk, aufschlagFromEkVk(ek, vk), betrag, "betrag", "aufschlag");
    }
    const aufschlag = roundMoney(value);
    const vk = ek > 0 ? vkFromEk(ek, aufschlag) : roundMoney(current.einzelpreis_netto ?? 0);
    return pricingSnapshot(
      ek,
      vk,
      aufschlag,
      ek > 0 ? aufschlagBetragFromEkVk(ek, vk) : roundMoney((ek * aufschlag) / 100),
      "prozent",
      "aufschlag",
    );
  }

  if (driver === "vk") {
    const vk = roundMoney(current.einzelpreis_netto ?? 0);
    return pricingSnapshot(
      ek,
      vk,
      aufschlagFromEkVk(ek, vk),
      aufschlagBetragFromEkVk(ek, vk),
      modus,
      "vk",
    );
  }

  if (modus === "betrag") {
    const betrag = roundMoney(current.aufschlag_betrag ?? 0);
    const vk = roundMoney(ek + betrag);
    return pricingSnapshot(ek, vk, aufschlagFromEkVk(ek, vk), betrag, "betrag", "aufschlag");
  }

  const aufschlag = roundMoney(current.aufschlag_prozent ?? 0);
  const vk = ek > 0 ? vkFromEk(ek, aufschlag) : roundMoney(current.einzelpreis_netto ?? 0);
  return pricingSnapshot(
    ek,
    vk,
    aufschlag,
    ek > 0 ? aufschlagBetragFromEkVk(ek, vk) : roundMoney((ek * aufschlag) / 100),
    "prozent",
    "aufschlag",
  );
}

/** Wechselt %/€ und rechnet den Aufschlag um, ohne den Verkaufspreis zu ändern. */
export function syncPricingAufschlagModus(
  current: ArticlePricingFields,
  modus: AufschlagModus,
): ArticlePricingFields & { preis_driver: PricingDriver; aufschlag_modus: AufschlagModus } {
  const next = normalizeAufschlagModus(modus);
  const ek = roundMoney(current.einkaufspreis_netto ?? 0);
  const driver = current.preis_driver ?? "aufschlag";
  const calculated = kalkulierterVk(current, current.einzelpreis_netto ?? 0);
  const vk = driver === "vk" ? roundMoney(current.einzelpreis_netto ?? 0) : calculated;
  const basis = driver === "vk" ? vk : calculated;
  return pricingSnapshot(
    ek,
    vk,
    aufschlagFromEkVk(ek, basis),
    aufschlagBetragFromEkVk(ek, basis),
    next,
    driver,
  );
}

/** @deprecated Use syncPricingField */
export function syncPricingOnEkChange(current: ArticlePricingFields, einkaufspreis: number) {
  return syncPricingField(current, "ek", einkaufspreis);
}

/** @deprecated Use syncPricingField */
export function syncPricingOnVkChange(current: ArticlePricingFields, verkaufspreis: number) {
  return syncPricingField(current, "vk", verkaufspreis);
}

/** @deprecated Use syncPricingField */
export function syncPricingOnAufschlagChange(current: ArticlePricingFields, aufschlagProzent: number) {
  return syncPricingField(current, "aufschlag", aufschlagProzent);
}
